import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ScanLine, QrCode, Barcode, Search, Globe, RefreshCw, Eye, AlertCircle, ShieldCheck, User as UserIcon, Building, ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '../../../shared/api/api.js';
import type { ScanRecord, Book } from '../../../shared/types/index.js';
import { useAuthStore } from '../../auth/store/useAuthStore.js';
import { useModalStore } from '../../../shared/store/useModalStore.js';
import { Scan30DayChart } from './Scan30DayChart.js';

export const ScanHistoryView: React.FC = () => {
  const { viewingBook: selectedBook, setViewingBook: setSelectedBook } = useModalStore();
  const { user } = useAuthStore();
  const [page, setPage] = useState(0);
  const limit = 20;

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['scanHistory', user?.id, user?.role, page],
    queryFn: () => api.getScanHistory(limit, page * limit),
    refetchInterval: 15000, // 15s auto-refresh
  });

  const history = data?.history || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  const handleInspectBook = async (uibn: string) => {
    try {
      const res = await api.getBookById(uibn);
      setSelectedBook(res.book);
    } catch {
      alert(`Buku dengan UIBN ${uibn} tidak ditemukan.`);
    }
  };

  const getMethodBadge = (method: string) => {
    switch (method) {
      case 'barcode_scanner':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
            <Barcode className="w-3.5 h-3.5" />
            <span>Kamera Barcode</span>
          </span>
        );
      case 'qr_scanner':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-100">
            <QrCode className="w-3.5 h-3.5" />
            <span>Kamera QR Code</span>
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
            <Search className="w-3.5 h-3.5" />
            <span>Pencarian UIBN</span>
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Histori Pemindaian Buku
            </h2>
            {user?.role === 'penulis' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                <UserIcon className="w-3 h-3" />
                <span>Katalog Penulis ({user.name})</span>
              </span>
            )}
            {user?.role === 'penerbit' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                <Building className="w-3 h-3" />
                <span>Katalog Penerbit ({user.organization || user.name})</span>
              </span>
            )}
            {user?.role === 'admin' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                <ShieldCheck className="w-3 h-3" />
                <span>Seluruh Katalog Nasional</span>
              </span>
            )}
          </div>
          <p className="text-xs sm:text-sm text-slate-500">
            Log pemindaian barcode, QR code, dan verifikasi nomor UIBN khusus untuk buku yang terdaftar di akun Anda.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            className="px-3.5 py-1.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-xl text-xs font-semibold shadow-2xs transition flex items-center gap-1.5 self-start sm:self-auto cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${isFetching ? 'animate-spin' : ''}`} />
            <span>Segarkan Log</span>
          </button>
        </div>
      </div>

      {/* 30-Day Scan Frequency Recharts Visualization Component */}
      <Scan30DayChart history={history} />

      {/* History Table Container */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mb-3" />
            <p className="text-xs text-slate-500">Memuat log histori pemindaian...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <AlertCircle className="w-8 h-8 text-rose-500 mx-auto mb-2" />
            <p className="text-sm font-semibold text-slate-800">Gagal memuat histori</p>
            <button
              onClick={() => refetch()}
              className="mt-3 px-3 py-1.5 text-xs bg-indigo-50 text-indigo-600 rounded-lg font-medium"
            >
              Coba Lagi
            </button>
          </div>
        ) : history.length === 0 ? (
          <div className="p-12 text-center">
            <ScanLine className="w-10 h-10 text-slate-300 mx-auto mb-2.5" />
            <h3 className="text-sm font-bold text-slate-800">Belum ada pemindaian tercatat</h3>
            <p className="text-xs text-slate-500 max-w-sm mx-auto mt-1">
              Gunakan pemindai di landing page untuk memindai barcode atau QR code buku.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider font-semibold text-[11px]">
                  <tr>
                    <th className="py-3.5 px-4">Waktu Pemindaian</th>
                    <th className="py-3.5 px-4">Judul & Penulis Buku</th>
                    <th className="py-3.5 px-4">Kode UIBN</th>
                    <th className="py-3.5 px-4">Metode Pemindaian</th>
                    <th className="py-3.5 px-4">Perangkat / IP</th>
                    <th className="py-3.5 px-4 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {history.map((item, idx) => (
                    <tr key={`scan-row-${item.id || idx}-${idx}`} className="hover:bg-slate-50/60 transition">
                      <td className="py-3 px-4 text-slate-500 whitespace-nowrap">
                        {new Date(item.scannedAt).toLocaleString('id-ID', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        })}
                      </td>
                      <td className="py-3 px-4 min-w-[220px]">
                        <div className="font-semibold text-slate-900 truncate max-w-[250px]" title={item.bookTitle}>
                          {item.bookTitle || 'Tanpa Judul'}
                        </div>
                        <div className="text-[11px] text-slate-400 truncate max-w-[250px]">
                          Penulis: {item.bookAuthor || 'Unknown'}
                        </div>
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className="font-mono font-bold text-indigo-700 bg-indigo-50/70 px-2 py-0.5 rounded border border-indigo-100/60">
                          {item.uibn}
                        </span>
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        {getMethodBadge(item.scanMethod)}
                      </td>
                      <td className="py-3 px-4 text-slate-400 whitespace-nowrap font-mono text-[11px]">
                        <div className="flex items-center gap-1.5">
                          <Globe className="w-3 h-3 text-slate-400" />
                          <span>{item.ipAddress || '127.0.0.1'}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => handleInspectBook(item.uibn)}
                          className="px-2.5 py-1 bg-slate-100 hover:bg-indigo-50 hover:text-indigo-600 rounded-lg text-slate-700 font-medium transition inline-flex items-center gap-1"
                        >
                          <Eye className="w-3 h-3" />
                          <span>Lihat Buku</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-4">
                <div className="text-[11px] text-slate-500 font-medium">
                  Menampilkan <span className="text-slate-900">{page * limit + 1}</span> sampai <span className="text-slate-900">{Math.min((page + 1) * limit, total)}</span> dari <span className="text-slate-900">{total}</span> pemindaian
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-indigo-600 font-bold text-[11px] disabled:opacity-40 disabled:cursor-not-allowed transition shadow-2xs cursor-pointer"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Sebelumnya</span>
                  </button>
                  <div className="flex items-center gap-1 px-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum = page - 2 + i;
                      if (page < 2) pageNum = i;
                      if (page > totalPages - 3) pageNum = totalPages - 5 + i;
                      if (pageNum < 0) return null;
                      if (pageNum >= totalPages) return null;
                      
                      return (
                        <button
                          key={`scan-page-btn-${pageNum}`}
                          onClick={() => setPage(pageNum)}
                          className={`w-8 h-8 rounded-xl text-[11px] font-bold transition flex items-center justify-center cursor-pointer ${
                            page === pageNum
                              ? 'bg-indigo-600 text-white shadow-sm'
                              : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                          }`}
                        >
                          {pageNum + 1}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-indigo-600 font-bold text-[11px] disabled:opacity-40 disabled:cursor-not-allowed transition shadow-2xs cursor-pointer"
                  >
                    <span className="hidden sm:inline">Berikutnya</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
